import { useEffect, useRef, useState } from "react";
import { ExternalLink, MapPin } from "lucide-react";
import "maplibre-gl/dist/maplibre-gl.css";

import { MAPLIBRE_OSM_STYLE } from "@/lib/map-style";

type MLMap = import("maplibre-gl").Map;

export type MiniMapPreviewProps = {
  lat: number | null | undefined;
  lng: number | null | undefined;
  /** Street address; combined with city/postal code into one formatted line. */
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
  /** Name used as the Google Maps search label when coordinates are absent. */
  name?: string | null;
  height?: number;
  zoom?: number;
  className?: string;
  /** Hide the address + directions footer and show the map only. */
  compact?: boolean;
};

/** Builds a single human-readable address line from the saved parts. */
export function formatAddressLine(
  address?: string | null,
  city?: string | null,
  postalCode?: string | null,
): string | null {
  const base = address?.trim() || "";
  const cityRaw = city ? city.replace(/[-_]+/g, " ").trim() : "";
  const cityLabel = cityRaw ? cityRaw.replace(/\b\w/g, (c) => c.toUpperCase()) : null;
  // Skip parts the street address already spells out (avoids "…Helsinki, Helsinki").
  const seen = base.toLowerCase();
  const pc = postalCode?.trim() || null;
  const tail = [pc && !seen.includes(pc.toLowerCase()) ? pc : null, cityLabel && !seen.includes(cityLabel.toLowerCase()) ? cityLabel : null]
    .filter(Boolean)
    .join(" ");
  const parts = [base || null, tail || null].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;

}

function isCoord(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

/**
 * Compact, read-only map preview for a single venue or business listing.
 * Renders nothing when no usable coordinates or address exist, so it can be
 * dropped into any public page without extra guards.
 */
export function MiniMapPreview({
  lat,
  lng,
  address,
  city,
  postalCode,
  name,
  height = 150,
  zoom = 14,
  className = "",
  compact = false,
}: MiniMapPreviewProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MLMap | null>(null);
  const [ready, setReady] = useState(false);

  const hasCoords = isCoord(lat) && isCoord(lng);
  const line = formatAddressLine(address, city, postalCode);

  const directionsHref = hasCoords
    ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
    : line
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([name, line].filter(Boolean).join(", "))}`
      : null;

  useEffect(() => {
    if (!hasCoords || !ref.current || mapRef.current) return;
    let cancelled = false;
    const node = ref.current;

    // Only pay for a map once the preview is actually near the viewport —
    // list pages can render dozens of cards at once.
    const init = () => {
      void import("maplibre-gl").then((mod) => {
        if (cancelled || !node || mapRef.current) return;
        const map = new mod.Map({
          container: node,
          style: MAPLIBRE_OSM_STYLE,
          center: [lng as number, lat as number],
          zoom,
          attributionControl: false,
          interactive: false,
        });
        new mod.Marker({ color: "#0f766e" }).setLngLat([lng as number, lat as number]).addTo(map);
        map.on("load", () => setReady(true));
        mapRef.current = map;
      });
    };

    let observer: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            observer?.disconnect();
            observer = null;
            init();
          }
        },
        { rootMargin: "300px" },
      );
      observer.observe(node);
    } else {
      init();
    }

    return () => {
      cancelled = true;
      observer?.disconnect();
      mapRef.current?.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCoords, lat, lng, zoom]);


  if (!hasCoords && !line) return null;

  return (
    <div className={`overflow-hidden rounded-xl border border-navy/10 bg-white ${className}`}>
      {hasCoords ? (
        <div className="relative" style={{ height }}>
          <div ref={ref} className="size-full" />
          {!ready ? <div className="absolute inset-0 animate-pulse bg-navy/5" /> : null}
        </div>
      ) : null}

      {!compact ? (
        <div className="flex items-start gap-2 px-3 py-2">
          <MapPin className="mt-0.5 size-3.5 shrink-0 text-teal" />
          <span className="min-w-0 flex-1 text-[12px] leading-snug text-navy/70">{line ?? "Location on map"}</span>
          {directionsHref ? (
            <a
              href={directionsHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex shrink-0 items-center gap-1 text-[11.5px] font-semibold text-royal underline underline-offset-2 hover:opacity-80"
            >
              Directions <ExternalLink className="size-3" />
            </a>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default MiniMapPreview;
